import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { uploadFile, deleteFile } from "modules/OnBoarding/actions";
import { Radio, Spin } from "antd";
import Button from "components/Button";
import PDFBadge from "components/icons/PDFBadge";
import DOCBadge from "components/icons/DOCBadge";
import PILBadge from "components/icons/PILBadge";
import DownloadIcon from "components/icons/DownArrowIcon";
import DeleteIcon from "components/icons/Delete";
import SuccessMessage from "utils/SuccessMessage"
import ErrorMessage from "utils/ErrorMessage"
import moment from "moment";
import ResumeDownload from "../Sections/ResumeDownload";
import {
  StyledDrawer,
  FooterFlex,
  DrawerListWrapper,
} from "../DrawerStyle";

import {
  ResumeCard,
  ResumeLeft,
  ResumeFileName,
  ResumeDate,
  ResumeActions,
  ResumeLimitNote, 
  UploadButtonWrapper
} from "../ResumeStyle";

const MAX_FILES = 6;
const MAX_SIZE_MB = 5;

const UploadResumeDrawer = ({
  drawerStatus,
  setDrawerStatus,
  resumeFiles = [],
  defaultId = "PIL",
  onSave,
  getStudentResumeData,
  studentId,
  studentUpdateAt,
  studentDetails,
  getPendingData,        
  pendingData,
}) => {
  const dispatch = useDispatch();
  const [localFiles, setLocalFiles] = useState([]);
  const [selectedId, setSelectedId] = useState(defaultId || "PIL");
  const [error, setError] = useState("");
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);  

  useEffect(() => {
    if (drawerStatus) {
      setLocalFiles(resumeFiles);
      setSelectedId(defaultId || "PIL");
      setError("");
    }
  }, [drawerStatus, defaultId, resumeFiles])

  const handleClose = () => {
    setDrawerStatus(false);
  };

  const handleSave = () => {
    setSubmitLoading(true);
    onSave(localFiles, selectedId);
    setDrawerStatus(false);
    setSubmitLoading(false);
  };

  const handleUpload = async (event) => {
    setLoadingResumes(true);
    const file = event.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    const validExts = ['pdf'];
    
    if (!validExts.includes(ext)) {
      setError('Only PDF files are allowed');
      event.target.value = "";
      return;
    }

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`File size should not exceed ${MAX_SIZE_MB}MB`);
      event.target.value = "";
      return;
    }

    if (localFiles.length >= MAX_FILES) {
      setError(`Maximum ${MAX_FILES-1} files allowed`);
      event.target.value = "";
      return;
    }

    setError("");

    try {
      const uploadedFileUrl = await dispatch(uploadFile(file, `resume_${Date.now()}.${ext}`));
      
      if (!uploadedFileUrl) {
        throw new Error('File upload failed');
      }

      const newFile = {
        id: Date.now(),
        file,
        name: file.name,
        type: ext.toUpperCase(),
        fileFormat: ext,
        uploadedOn: new Date().toLocaleDateString("en-GB"),
        url: uploadedFileUrl,
        _raw: {
          id: null,
          resumeName: file.name,
          resumeType: "UPLOADED",
          fileUrl: { url: uploadedFileUrl },
          isDefault: false,
        }
      };

      setLocalFiles((prev) => [...prev, newFile]);
      setSubmitLoading(false);
    } catch (error) {
      console.error('Upload error:', error);
      ErrorMessage('Failed to upload resume');
    } finally {
      event.target.value = "";
      setLoadingResumes(false);
    }
  };

  const handleDelete = async (id) => {
    setLoadingResumes(true);
    const fileToDelete = localFiles.find(f => f.id === id);
    if (!fileToDelete) return;

    try {
      if (fileToDelete.url && !fileToDelete.url.startsWith('blob:')) {
        const success = await dispatch(deleteFile(fileToDelete.url));
        if (!success) {
          throw new Error('Failed to delete file from server');
        }
      }

      setLocalFiles((prev) => {
        const updated = prev.filter((f) => f.id !== id);

        if (selectedId === id) {
          let newDefault =
            updated.find(f => f.isDefault)?.id ||
            updated[0]?.id ||
            "PIL";

          setSelectedId(newDefault);
        }

        return updated;
      });

      SuccessMessage('Resume deleted successfully!');
    } catch (error) {
      console.error('Delete error:', error);
      ErrorMessage('Failed to delete resume');
    } finally {
      setLoadingResumes(false);
    }
  };

  const downloadURI = async (file) => {
    try {
      const response = await fetch(file.url)
      if (!response.ok) {
        throw new Error('Failed to download file')
      }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading file:', error)
    }
  }

  const renderUserFiles = () =>
    localFiles.map((file) => {
      const isSelected = selectedId === file.id;
      const isPdf = file.fileFormat === "pdf" ;
      const isDefault = file.isDefault;
      const isTemporary = file?._raw?.id === null;
      if (file.type !== "SYSTEM") {
        return (
          <ResumeCard
            key={file.id}
            style={{
              cursor: file.isPending || isTemporary ? "not-allowed" : "pointer",
              background: isDefault ? "#DAECFF" : "#FFFFFF",
              border: `1px solid ${isSelected && !isDefault ? "#3249D7" : "#E6E7EA"}`,
            }}
            onClick={() => {
              if (!file.isPending && !isTemporary) {
                setSelectedId(file.id);
              }
            }}
          >
            <ResumeLeft>
              {isPdf ? <PDFBadge /> : <DOCBadge />}
              <div>
                <ResumeFileName>{file.name}</ResumeFileName>
                <ResumeDate>Uploaded on {file.uploadedOn}</ResumeDate>
              </div>
            </ResumeLeft>

            <ResumeActions>
              {file.isDefault && (
                <div
                  style={{
                    width: 67,
                    height: 26,
                    borderRadius: 5,
                    background: "#F2F7FF",
                    border: "1px solid #DAE6FF",
                    color: "#3A66FF",
                    fontSize: 12,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 10,
                  }}
                >
                  Default
                </div>
              )}
              {file.isPending && (
                <div
                  style={{
                    marginTop: 4,
                    padding: "2px 8px",
                    borderRadius: 6,
                    background: "#FFF7E6",
                    color: "#FA8C16",
                    fontSize: 12,
                    fontWeight: 600,
                    display: "inline-block",
                  }}
                >
                  Pending Approval
                </div>
              )}

              <span
                onClick={(e) => {
                  e.stopPropagation();
                  downloadURI(file);
                }}
              >
                <DownloadIcon />
              </span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(file.id);
                }}
              >
                <DeleteIcon />
              </span>
              <Radio
                checked={isSelected}
                disabled={file.isPending || isTemporary}
                onChange={() => {
                  if (!file.isPending && !isTemporary) {
                    setSelectedId(file.id);
                  }
                }}
              />
            </ResumeActions>
          </ResumeCard>
        );
      } else {
        return (
          <ResumeCard
            key={file.id}
            style={{
              cursor: file.isPending || isTemporary ? "not-allowed" : "pointer",
              background: isDefault ? "#DAECFF" : "#FFFFFF",
              border: `1px solid ${isSelected && !isDefault ? "#3249D7" : "#E6E7EA"}`,
            }}
            onClick={() => {
              if (!file.isPending && !isTemporary) {
                setSelectedId(file.id);
              }
            }}
          >
            <ResumeLeft>
              <PILBadge />
              <div>
                <ResumeFileName>PIL Resume</ResumeFileName>
                <ResumeDate>Updated on {moment(studentUpdateAt).format("DD/MM/YYYY")}</ResumeDate>
              </div>
            </ResumeLeft>

            <ResumeActions>
              {file.isDefault && (
                <div
                  style={{
                    width: 67,
                    height: 26,
                    borderRadius: 5,
                    background: "#F2F7FF",
                    border: "1px solid #DAE6FF",
                    color: "#3A66FF",
                    fontSize: 12,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 10,
                  }}
                >
                  Default
                </div>
              )}
              <div
                style={{
                  background: "#F0F4FF",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#3249D7",
                  letterSpacing: "0.3px",
                }}
              >
                Sys. Generated
              </div>
              <ResumeActions>
                {studentDetails ? (
                  <ResumeDownload studentData={studentDetails}>
                    {({ url, fileName }) => (
                      <a
                        href={url || undefined}
                        download={fileName}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center' }}
                      >
                        <DownloadIcon />
                      </a>
                    )}
                  </ResumeDownload>
                ) : (
                  <span onClick={null}>
                    <DownloadIcon />
                  </span>
                )}
              </ResumeActions>
              <Radio
                checked={isSelected}
                disabled={file.isPending || isTemporary}
                onChange={() => {
                  if (!file.isPending && !isTemporary) {
                    setSelectedId(file.id);
                  }
                }}
              />
            </ResumeActions>
          </ResumeCard>
        );
      }
    });

  const content = () => (
    <>
      <DrawerListWrapper>
        <DrawerListWrapper>
          {loadingResumes ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
            </div>
          ) : (
            renderUserFiles()
          )}
        </DrawerListWrapper>
      </DrawerListWrapper>

      <input
        id="upload-resume-input-drawer"
        type="file"
        accept=".pdf"
        style={{ display: "none" }}
        onChange={handleUpload}
      />

      <div style={{ marginTop: 16 }}>
        {localFiles.length < MAX_FILES ? (
          <UploadButtonWrapper>
            <Button.Secondary
              text="Upload New Resume"
              onClick={() =>
                document
                  .getElementById("upload-resume-input-drawer")
                  .click()
              }
            />
          </UploadButtonWrapper>
        ) : (
          <ResumeLimitNote>
            You've reached the limit of 5 resumes. <br />
            To upload a new file, please remove an existing resume.
          </ResumeLimitNote>
        )}
      </div>

      {error && (
        <div style={{ color: "red", marginTop: 8, fontSize: 13 }}>
          {error}
        </div>
      )}
    </>
  );

  const footer = () => (
    <FooterFlex right>
      <Button.Secondary text="Cancel" onClick={handleClose} />
      <Button.Primary
        text="Save"
        disable={submitLoading}
        disabled={!selectedId && localFiles.length === 0 && submitLoading}
        onClick={handleSave}
      />
    </FooterFlex>
  );

  return (
    <StyledDrawer
      width="720px"
      title="Upload New Resume"
      placement="right"
      closable
      onClose={handleClose}
      visible={drawerStatus}
      content={content()}
      footer={footer()}
    />
  );
};

export default UploadResumeDrawer;
